import React, { useEffect, useState } from "react";

import {
  ErrorMessage,
  PrimaryButton,
  SingleSelect,
  Text,
  Textfield,
} from "widgets";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  INDIAN_CURRENCY_ID,
  PAYMENT_MODE,
  PAYMENT_MODE_OPTIONS,
  PLUTUS_PAYMENT_LINK_SOURCE,
} from "constants/config";
import { useCreatePaymentLink } from "store/backend/assets";
import { toast } from "react-fox-toast";
import Loader from "Shared/Loader/Loader";
import CashAndChequePayment from "components/OfflinePayments/OfflinePayments";

import styles from "./OnlinePayment.module.scss";

const AMOUNT_TYPES = [
  {
    key: 1,
    label: "Full Amount",
    value: "Full Amount",
  },
  {
    key: 2,
    label: "Custom Amount",
    value: "Custom Amount",
  },
];
const FULL_AMOUNT = "Full Amount";
const CUSTOM_AMOUNT = "Custom Amount";
const CUSTOM_AMOUNT_INDEX = 1;

const getDefaultSelected = (totalAmount, payableAmount) => {
  if (totalAmount === payableAmount) {
    return FULL_AMOUNT;
  }
  return CUSTOM_AMOUNT;
};

const getPayableAmount = (
  amountType,
  totalAmount,
  payableAmount,
  customAmount,
  headAmount
) => {
  if (amountType === FULL_AMOUNT) {
    return totalAmount;
  }

  if (amountType === CUSTOM_AMOUNT) {
    if (headAmount) {
      return Number(customAmount);
    }
    if (customAmount) {
      return Number(customAmount);
    }
    return payableAmount;
  }

  return payableAmount;
};

const handleAmountTypeChange = (
  event,
  setAmountType,
  setCustomAmount,
  setPaymentLink
) => {
  const selectedValue = event.target.value;
  setAmountType(selectedValue);
  setCustomAmount("");
  setPaymentLink("");
};

const handleCreatePaymentLink = (
  orderId,
  amountType,
  payableAmount,
  customAmount,
  setPaymentLink,
  setIsLinkCopied,
  createPaymentLink,
  isDownpayment,
  paymentFlowTypeId,
  headAmount
) => {
  const amount = isDownpayment
    ? payableAmount
    : amountType === FULL_AMOUNT
    ? payableAmount
    : amountType === CUSTOM_AMOUNT && headAmount
    ? customAmount
    : customAmount;

  const paymentTypeId = isDownpayment
    ? AMOUNT_TYPES[CUSTOM_AMOUNT_INDEX].key
    : AMOUNT_TYPES.find((type) => type.value === amountType)?.key;

  const payload = {
    order_id: orderId,
    payment_link_amount: amount,
    currency_id: INDIAN_CURRENCY_ID,
    payment_type_id: paymentTypeId,
    payment_link_source_id: PLUTUS_PAYMENT_LINK_SOURCE,
    payment_flow_type_id: paymentFlowTypeId,
  };

  createPaymentLink(payload, {
    onSuccess: (paymentLink) => {
      setIsLinkCopied(false);
      setPaymentLink(paymentLink);
    },
  });
};

const handleCopyLink = (paymentLink, setIsLinkCopied, setPaymentLink) => {
  navigator.clipboard.writeText(paymentLink);
  setIsLinkCopied(true);
  setPaymentLink("");
  toast.success("Copied Payment Link!");
};

const isFormValid = (
  amountType,
  payableAmount,
  customAmount,
  isDownpayment,
  headAmount
) => {
  if (isDownpayment) {
    return payableAmount > 0;
  }

  if (amountType === FULL_AMOUNT) {
    return payableAmount > 0;
  }

  if (amountType === CUSTOM_AMOUNT) {
    if (!customAmount) {
      return false;
    }

    if (headAmount) {
      return (
        Number(customAmount) >= Number(headAmount) &&
        Number(customAmount) <= Number(payableAmount)
      );
    }
    return (
      Number(customAmount) > 0 && Number(customAmount) <= Number(payableAmount)
    );
  }

  return false;
};

const OnlinePayment = ({
  totalAmount,
  payableAmount,
  orderId,
  isDownpayment,
  paymentFlowTypeId,
  salesPersonId,
  headAmount,
}) => {
  const [amountType, setAmountType] = useState(
    getDefaultSelected(totalAmount, payableAmount)
  );
  const [customAmount, setCustomAmount] = useState(null);
  const [paymentLink, setPaymentLink] = useState("");
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODE_OPTIONS[0].value);
  const {
    mutate: createPaymentLink,
    isPending: isCreatePaymentLinkLoading,
    isError,
    error,
  } = useCreatePaymentLink();

  useEffect(() => {
    setPaymentLink("");
  }, [amountType, customAmount, paymentMode]);

  return (
    <div className={styles.onlinePaymentContainer}>
      {!isDownpayment ? (
        <div className={styles.field}>
          <Text>Select Amount Type</Text>
          <SingleSelect
            handleChange={(event) =>
              handleAmountTypeChange(
                event,
                setAmountType,
                setCustomAmount,
                setPaymentLink
              )
            }
            value={amountType}
            defaultSelected={amountType}
            menuItems={
              totalAmount === payableAmount
                ? AMOUNT_TYPES
                : [AMOUNT_TYPES[CUSTOM_AMOUNT_INDEX]]
            }
          />
        </div>
      ) : (
        <div className={styles.field}>
          <SingleSelect
            value={CUSTOM_AMOUNT}
            menuItems={[AMOUNT_TYPES[CUSTOM_AMOUNT_INDEX]]}
            disabled
          />
        </div>
      )}
      <div className={styles.field}>
        <Text>Payment Amount</Text>
        <Textfield
          value={
            isDownpayment
              ? payableAmount
              : amountType === CUSTOM_AMOUNT
              ? customAmount
              : amountType === FULL_AMOUNT
              ? totalAmount
              : ""
          }
          disabled={amountType === FULL_AMOUNT || isDownpayment}
          handleOnChange={(event) => {
            const value = event.target.value;
            const validAmount = value
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*?)\..*/g, "$1");

            setCustomAmount(validAmount);
          }}
          label="Enter Custom Amount"
        />
        {amountType === CUSTOM_AMOUNT &&
          headAmount &&
          Number(customAmount) < Number(headAmount) && (
            <ErrorMessage>
              Please collect an amount more than or equal to {headAmount} INR
            </ErrorMessage>
          )}

        {isDownpayment ? (
          payableAmount > 0
        ) : amountType === CUSTOM_AMOUNT && !headAmount && !customAmount ? (
          <ErrorMessage>Please enter a valid number</ErrorMessage>
        ) : null}
      </div>
      <div className={styles.field}>
        <Text>Select Payment Mode</Text>
        <SingleSelect
          value={paymentMode}
          menuItems={PAYMENT_MODE_OPTIONS}
          handleChange={(event) => setPaymentMode(event.target.value)}
          tag="Select Payment Mode"
        />
      </div>
      <div className={styles.createPaymentLinkContainer}>
        {isCreatePaymentLinkLoading ? (
          <Loader />
        ) : (
          paymentMode === PAYMENT_MODE.ONLINE && (
            <PrimaryButton
              onClick={() =>
                handleCreatePaymentLink(
                  orderId,
                  amountType,
                  payableAmount,
                  customAmount,
                  setPaymentLink,
                  setIsLinkCopied,
                  createPaymentLink,
                  isDownpayment,
                  paymentFlowTypeId,
                  headAmount
                )
              }
              disabled={
                !isFormValid(
                  amountType,
                  payableAmount,
                  customAmount,
                  isDownpayment,
                  headAmount
                )
              }
            >
              Create Payment Link
            </PrimaryButton>
          )
        )}
        {paymentMode !== PAYMENT_MODE.ONLINE && (
          <CashAndChequePayment
            orderId={orderId}
            payableAmount={getPayableAmount(
              amountType,
              totalAmount,
              payableAmount,
              customAmount,
              headAmount
            )}
            createdBy={salesPersonId}
            paymentMode={paymentMode}
          />
        )}
        {paymentLink && (
          <div
            className={styles.paymentLinkContainer}
            onClick={() =>
              handleCopyLink(paymentLink, setIsLinkCopied, setPaymentLink)
            }
          >
            <div className={styles.copyLinkText}>Copy Link</div>
            <ContentCopyIcon className={styles.copyIcon} />
          </div>
        )}
      </div>
      {isError && <ErrorMessage>{error.message}</ErrorMessage>}
    </div>
  );
};

export default OnlinePayment;
